/*
 * A product's licensed age range as the catalogue holds it (KIN-2126): the minimum is
 * `minAgeMonths` months plus `minAgeDays` days, and the days can be negative — Varicella's
 * minimum is twelve months less a four-day grace period. `underAgeMonths` is the first age
 * the product no longer covers. Shown beside each product once "Show all products" lifts the
 * age filter, so the clinician sees they are going off-range (KIN-2127).
 */
const MONTHS_PER_YEAR = 12;
const DAYS_PER_WEEK = 7;

const plural = (count, unit) => `${count} ${unit}${count === 1 ? "" : "s"}`;

const formatMonths = (months) =>
  months % MONTHS_PER_YEAR === 0
    ? plural(months / MONTHS_PER_YEAR, "year")
    : plural(months, "month");

const formatDays = (days) =>
  days % DAYS_PER_WEEK === 0
    ? plural(days / DAYS_PER_WEEK, "week")
    : plural(days, "day");

/** The minimum age as text, or null when the product is licensed from birth. */
const formatMinimumAge = (minAgeMonths, minAgeDays) => {
  const months = minAgeMonths ?? 0;
  const days = minAgeDays ?? 0;

  if (months === 0 && days === 0) return null;
  if (months === 0) return formatDays(days);
  if (days === 0) return formatMonths(months);

  return days > 0
    ? `${formatMonths(months)} and ${plural(days, "day")}`
    : `${formatMonths(months)} less ${plural(-days, "day")}`;
};

/** e.g. "6 weeks – <10 years", "12 years and over", "Under 2 years"; null with no range. */
export const getAgeRangeLabel = (product) => {
  const minimum = formatMinimumAge(product?.minAgeMonths, product?.minAgeDays);
  const underAgeMonths = product?.underAgeMonths;
  const maximum = underAgeMonths ? formatMonths(underAgeMonths) : null;

  if (minimum && maximum) return `${minimum} – <${maximum}`;
  if (minimum) return `${minimum} and over`;
  if (maximum) return `Under ${maximum}`;

  return null;
};
